/**
 * Settings › Security — turning two-step verification ON.
 *
 * beginSetup (fresh secret + QR) → scan / type the key → confirm the current
 * password and a code → `enable` → show recovery codes once → done.
 *
 * `enable` replaces the caller's session with an aal2 one; in the native
 * shell the new session id comes back in the body and must be stored before
 * the next request, or the app would keep presenting the revoked one.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, FormField, Input, PasswordInput } from '@jumpstone/ui-react';
import { getTrpcClient } from '../../lib/trpc.js';
import { isNative } from '../../lib/platform.js';
import { nativeClientInput } from '../../lib/auth/native_shell_auth.js';
import { storeReplacementSession } from './store_replacement_session.js';
import {
  classifyTwoFactorError,
  isAuthenticatorCode,
  normaliseTwoFactorCode,
  type TwoFactorErrorKind,
} from '../../lib/auth/two_factor_logic.js';
import { AuthenticatorSetupCard } from '../../components/auth/authenticator_setup_card.js';
import { RecoveryCodesPanel } from '../../components/auth/recovery_codes_panel.js';
import { TwoFactorError } from '../../components/auth/two_factor_error.js';

type Step =
  | { kind: 'start' }
  | { kind: 'scan'; secret: string; qrSvg: string }
  | { kind: 'codes'; codes: string[] };

export function SecurityEnableFlow({
  onCancel,
  onEnabled,
}: {
  onCancel: () => void;
  /** Called after the user has seen and confirmed their recovery codes. */
  onEnabled: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [step, setStep] = useState<Step>({ kind: 'start' });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<TwoFactorErrorKind | null>(null);

  async function begin(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const material =
        await getTrpcClient().wageproTwoFactor.beginSetup.mutate();
      setStep({
        kind: 'scan',
        secret: material.secret,
        qrSvg: material.qr_svg,
      });
    } catch (err) {
      setError(classifyTwoFactorError(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(): Promise<void> {
    const normalised = normaliseTwoFactorCode(code);
    if (!isAuthenticatorCode(normalised)) {
      setError('invalid_code');
      return;
    }
    setBusy(true);
    setError(null);
    const native = isNative();
    try {
      const result = await getTrpcClient().wageproTwoFactor.enable.mutate({
        current_password: password,
        code: normalised,
        ...nativeClientInput(native),
      });
      if (!(await storeReplacementSession(result, native))) return;
      setPassword('');
      setStep({ kind: 'codes', codes: result.backup_codes });
    } catch (err) {
      setError(classifyTwoFactorError(err));
    } finally {
      setBusy(false);
    }
  }

  if (step.kind === 'codes') {
    return (
      <RecoveryCodesPanel
        codes={step.codes}
        doneLabel={t('two_factor.done')}
        onDone={onEnabled}
      />
    );
  }

  if (step.kind === 'start') {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-[color:var(--ink-muted)]">
          {t('two_factor.enable_intro')}
        </p>
        {error !== null && <TwoFactorError kind={error} />}
        <div className="flex gap-2">
          <Button variant="primary" loading={busy} onClick={() => void begin()}>
            {t('two_factor.get_started')}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void confirm();
      }}
      className="flex flex-col gap-4"
    >
      <AuthenticatorSetupCard qrSvg={step.qrSvg} secret={step.secret} />
      <FormField label={t('two_factor.code_label')} htmlFor="enable-2fa-code">
        <Input
          id="enable-2fa-code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoComplete="one-time-code"
          inputMode="numeric"
          disabled={busy}
          className="mono max-w-[200px]"
        />
      </FormField>
      <FormField
        label={t('two_factor.current_password_label')}
        htmlFor="enable-2fa-password"
      >
        <PasswordInput
          id="enable-2fa-password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={busy}
          className="max-w-sm"
          labels={{
            show_password: t('login.show_password'),
            hide_password: t('login.hide_password'),
          }}
        />
      </FormField>
      {error !== null && <TwoFactorError kind={error} />}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" loading={busy}>
          {t('two_factor.turn_on')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={busy}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
